import { useEffect, useMemo, useState } from 'react'
import { Alert, Spinner, Typography } from '@material-tailwind/react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import UploadAgamiInterface from './UploadAgamiInterface'
import Container from '../components/Container'
import GoBack from '../components/GoBack'
import { useSessionContext } from '../contexts/SessionContext'
import { usePageTitle } from '../hooks/toolkit'
import { getSpotTypes } from '../utils/helpers/supabase.helpers'
import { getUserGeoXpSpotCount } from '../utils/helpers/user-spot.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

const UploadSingleSpot: React.FC = () => {
  const { t } = useTranslation()
  usePageTitle('Upload Spot | Agami')
  const { darkMode } = useDarkMode()
  const { loading, user } = useSessionContext()

  const spotLimit = user?.uploadLimit

  const [uploadLimitReached, setUploadLimitReached] = useState(false)
  const [countLoading, setCountLoading] = useState(true)
  const [availableSpotTypes, setAvailableSpotTypes] = useState<Record<number, string>>()

  const getAvailableSpotTypes = async () => {
    const { data } = await getSpotTypes()
    const types = (data as { id: number; label: string }[] | undefined)?.reduce(
      (acc: Record<number, string>, curr: { id: number; label: string }) => ({
        ...acc,
        [curr.id]: curr.label,
      }),
      {}
    )
    setAvailableSpotTypes(types)
  }

  const getSpotCount = async () => {
    const { count } = await getUserGeoXpSpotCount()
    setUploadLimitReached((count ?? 0) >= Number(spotLimit))
    setCountLoading(false)
  }

  useEffect(() => {
    if (loading || !user?.id) {
      return
    }

    getAvailableSpotTypes()
    getSpotCount()
  }, [loading, user])

  const isLoading = useMemo(() => loading || countLoading, [loading, countLoading])

  return (
    <Container direction="col">
      <div className="mb-2 self-start">
        <GoBack />
      </div>
      <Typography
        variant="lead"
        className="text-primary dark:text-white"
        data-testid="upload-single-agami-title"
      >
        {t('upload.title')}
      </Typography>

      {isLoading && (
        <div className="w-full my-10">
          <Spinner className="h-12 w-12 mx-auto" />
        </div>
      )}

      {!isLoading && uploadLimitReached && (
        <div className="my-10 text-center">
          <Alert variant={darkMode ? 'outlined' : 'ghost'} color="orange">
            <Typography variant="paragraph">
              {t('upload.uploadLimitReached')} You can check them in your{' '}
              <span className="font-bold underline">
                <Link to="/">{t('navigation.dashboard')}</Link>
              </span>
              .
            </Typography>
          </Alert>
        </div>
      )}

      {!isLoading && availableSpotTypes && !uploadLimitReached && (
        <UploadAgamiInterface availableSpotTypes={availableSpotTypes} />
      )}
    </Container>
  )
}

export default UploadSingleSpot
